import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../redux-store";
import { orderBy } from "lodash";
import * as types from "./types";
import { CargoView } from "../../data/cargo-views/types";
import { allSortProperties } from "./types";
import { selectFeature } from "../../features/selectors";

// Declare as constant to avoid unnecessary rerenderings.
const empty: string[] = [];

export const selectDraggedCargoId = (state: RootState) =>
  state.app.dispatching.dragState?.cargoId;

export const selectSourceRouteId = (state: RootState) =>
  state.app.dispatching.dragState?.sourceRouteId;

export const selectCurrentRouteId = (state: RootState) =>
  state.app.dispatching.dragState?.currentRouteId;

const selectOriginalCargoOrder = (routeId: string | null) =>
  createSelector(
    (state: RootState) =>
      routeId ? state.data.routeViewModels.entities[routeId]?.cargoOrder : [],
    (state: RootState) => state,
    (routeViewModels, state) =>
      routeId ? routeViewModels : selectOriginalUnassignedCargoIdsNew(state)
  );

export const selectRouteCargoIds =
  (routeId: string | null) => (state: RootState) =>
    state.app.dispatching.dragState?.routeCargoIds[routeId || ""] ||
    selectOriginalCargoOrder(routeId)(state) ||
    empty;

export const selectUnassignedCargoIds = selectRouteCargoIds(null);

export const selectRouteVisibleCargoIds = (routeId: string | null) =>
  createSelector(
    selectRouteCargoIds(routeId),
    (state: RootState) => state.data.cargoViews.entities,
    (state: RootState) => state.routes.relevantCargoFilter,
    (cargoIds, cargoViews, relevantCargoFilter) => {
      // Only the cargos on the shown routes are filtered — never the
      // undispatched list (routeId === null).
      if (!relevantCargoFilter || routeId === null) {
        return cargoIds;
      }
      return cargoIds.filter((id) => {
        const cargoView: CargoView | undefined = cargoViews[id];
        // Keep cargos that aren't loaded yet rather than hiding them.
        if (!cargoView) {
          return true;
        }
        if (
          relevantCargoFilter.clientId &&
          cargoView.clientId !== relevantCargoFilter.clientId
        ) {
          return false;
        }
        // Exact-phrase, case-insensitive substring match — mirrors the
        // descriptions_search backend filter (word order matters).
        if (
          relevantCargoFilter.descriptionsSearch &&
          !(cargoView.description ?? "")
            .toLowerCase()
            .includes(relevantCargoFilter.descriptionsSearch.toLowerCase())
        ) {
          return false;
        }

        if (relevantCargoFilter.cargoRef) {
          if (
            cargoView.cargoRef
              ?.toLowerCase()
              .includes(relevantCargoFilter.cargoRef.toLowerCase())
          ) {
            return false;
          }
        }

        if (relevantCargoFilter.cargoTagId) {
          // "none" matches cargos with no tag; otherwise match the exact tag.
          const hasNoTag = !cargoView.cargoTagId;
          if (relevantCargoFilter.cargoTagId === "none") {
            if (!hasNoTag) {
              return false;
            }
          } else if (cargoView.cargoTagId !== relevantCargoFilter.cargoTagId) {
            return false;
          }
        }
        return true;
      });
    }
  );

export const selectFilterOptions =
  (type: types.FilterType) => (state: RootState) =>
    state.app.dispatching.teamFilterOptions[type];

export const selectFilteredTeamIds = (state: RootState) =>
  selectFilterOptions("routes")(state);

export const selectCargoFilterTeamIds = (state: RootState) =>
  state.app.dispatching.teamFilterOptions.cargos;

export const selectCargoTagFilterIds = (state: RootState) =>
  state.app.dispatching.cargoTagFilterIds;

export const selectAreCargoFiltersEnabled = (state: RootState) =>
  !!selectCargoFilterTeamIds(state).length ||
  !!selectCargoTagFilterIds(state).length;

export const selectAreRouteFiltersEnabled = (state: RootState) =>
  !!selectFilteredTeamIds(state).length;

export const selectTruckFilterIds = (state: RootState) =>
  state.app.dispatching.truckFilterIds;

export const selectTrailerFilterIds = (state: RootState) =>
  state.app.dispatching.trailerFilterIds;

export const selectDriverFilterIds = (state: RootState) =>
  state.app.dispatching.driverFilterIds;

export const selectShouldClearButtonBeDisabled = createSelector(
  selectAreRouteFiltersEnabled,
  (state: RootState) => state.routes.sortProperty,
  (state: RootState) => state.routes.routesSorting,
  selectFeature("multi-property-route-sort"),
  (filtersEnabled, sortProperty, routesSorting, multiSortEnabled) =>
    !filtersEnabled &&
    (multiSortEnabled ? !routesSorting.length : !sortProperty)
);

export const selectRoutesSearchDates = (state: RootState) =>
  state.app.dispatching.routeSearchDates;

export const selectUndispatchedCargosSort = (state: RootState) =>
  state.app.dispatching.unassignedCargoSortingNew;

export const selectUnassignedCargoSearchText = (state: RootState) =>
  state.app.dispatching.unassignedCargoSearchText;

const selectUndispatchedSortNew = (state: RootState) =>
  state.app.dispatching.unassignedCargoSortingNew;

// A cargo's tag matches when the whole search, or any whitespace-separated
// part of it, is a (case-insensitive) substring of the tag name. `search` is
// already trimmed and lower-cased by the callers.
const matchesTagName = (
  tagName: string | null | undefined,
  search: string
) => {
  if (!tagName) {
    return false;
  }
  const name = tagName.toLowerCase();
  return search
    .split(/\s+/)
    .filter(Boolean)
    .some((part) => name.includes(part));
};

export const matchesSearch = (
  cargo: CargoView,
  search: string,
  clientName?: string | null,
  cargoTagName?: string | null
) => {
  const s = (v: string | null | undefined) =>
    v?.toLowerCase().includes(search) ?? false;
  return (
    s(cargo.invoiceRef) ||
    s(cargo.cargoRef) ||
    s(cargo.description) ||
    s(cargo.firstStop.city) ||
    s(cargo.firstStop.place) ||
    s(cargo.firstStop.countryCode) ||
    s(cargo.firstStop.postcode) ||
    s(cargo.lastStop?.city) ||
    s(cargo.lastStop?.place) ||
    s(cargo.lastStop?.countryCode) ||
    s(cargo.lastStop?.postcode) ||
    s(clientName) ||
    matchesTagName(cargoTagName, search)
  );
};

export const selectOriginalUnassignedCargoIdsNew = createSelector(
  (state: RootState) => state.app.dispatching.teamFilterOptions.cargos,
  selectUndispatchedSortNew,
  (state: RootState) => state.data.cargoViews.entities,
  (state: RootState) => state.app.dispatching.originalUnassignedCargoIds,
  (state: RootState) => state.data.clients.entities,
  (state: RootState) => state.app.dispatching.unassignedCargoSearchText,
  (state: RootState) => state.data.orgSettings.cargoTags,
  selectCargoTagFilterIds,
  (
    teamFilters,
    sorting,
    entities,
    originalUnassignedCargoIds,
    clients,
    searchText,
    cargoTags,
    cargoTagFilterIds
  ) => {
    const search = searchText.trim().toLowerCase();
    const tagNameById = new Map(
      cargoTags.map((tag) => [tag.id, tag.description])
    );
    const cargos = originalUnassignedCargoIds
      .map((id) => entities[id])
      .filter((x) => x && !x.smartMatch)
      .filter((x) => teamFilters.includes(x.assignedTeamId))
      .filter(
        (x) =>
          !cargoTagFilterIds.length ||
          (x.cargoTagId && cargoTagFilterIds.includes(x.cargoTagId))
      )
      .filter(
        (x) =>
          !search ||
          matchesSearch(
            x,
            search,
            x.clientId ? clients[x.clientId]?.client : null,
            x.cargoTagId ? tagNameById.get(x.cargoTagId) : null
          )
      );

    if (sorting.length > 0) {
      const iteratees = sorting.map(
        ({ property: sortProperty }) =>
          (cargo: CargoView) => {
            switch (sortProperty) {
              case types.pickupSort:
                return cargo.firstStop.date;
              case types.dropoffSort:
                return cargo.lastStop?.date;
              case types.ldmSort:
                return +cargo.totalLm;
              case types.createdAtSort:
                return cargo.createdAt;
              case types.pickupPostCodeSort:
                return `${cargo.firstStop.countryCode}${cargo.firstStop.postcode}`;
              case types.dropoffPostCodeSort:
                return `${cargo.lastStop?.countryCode}${cargo.lastStop?.postcode}`;
              case types.clientSort:
                return cargo.clientId ? clients[cargo.clientId]?.client : null;
              case types.label:
                return cargo.cargoTagId || "";
              default:
                throw new Error("Unknown sort id");
            }
          }
      );
      const orders = sorting.map(
        ({ order }) => order.toLowerCase() as "asc" | "desc"
      );

      const sortedCargos = orderBy(cargos, iteratees, orders);
      return sortedCargos.map((x) => x.id);
    } else {
      return cargos.map((x) => x.id);
    }
  }
);

export const selectUndispatchedUnusedSortProperties = createSelector(
  selectUndispatchedSortNew,
  (usedSortSelections) =>
    allSortProperties.filter(
      (x) => !usedSortSelections.find((y) => y.property === x)
    )
);
